import { FieldLayerWellsGroup } from '@/api/fields';
import { getLayer } from '@/api/layers';
import { layerDefaultWellProfilesApi } from '@/api/layerDefaultWellProfiles';
import type { LayerDefaultWellProfileCoefs } from '@/api/layerDefaultWellProfiles';
import { LayerProductionImportCsvModal } from '@/components/Layers/LayerProductionImportCsvModal';
import {
  DefaultProfilePoint,
  HistoricalProductionPoint,
  buildActiveWellsByYear,
  buildDefaultProfilesFromCoefs,
  collectCalendarYears,
  computeLiquid,
  computeWaterCut,
  mergeHistoryChartData,
} from '@/utils/layerProductionCharts';
import { formatThousandVolumeTooltip, SCENARIO_UNIT_TONS } from '@/utils/scenarioUnits';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface LayerProductionModalProps {
  open: boolean;
  onClose: () => void;
  layerId: string;
  layerName: string;
  wells: FieldLayerWellsGroup[];
  canManage?: boolean;
  onDataUpdated?: () => void;
}

const HISTORY_CHART_HEIGHT = 400;
const PROFILE_CHART_HEIGHT = 400;
const OIL_COLOR = '#5D4037';
const GAS_COLOR = '#EF6C00';
const LIQUID_COLOR = '#1565C0';
const WC_COLOR = '#C62828';
const WELLS_BAR_COLOR = '#8884d8';
const PRODUCTION_AXIS_LABEL = 'Oil / Gas / Liquid (1000 t)';
const PROFILE_RATES_AXIS_LABEL = 'Oil / Gas / Liquid (1000 t)';

const EmptyChartPlaceholder: React.FC<{ title: string; height?: number }> = ({ title, height = HISTORY_CHART_HEIGHT }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Box
      sx={{
        height,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        No data
      </Typography>
    </Box>
  </Box>
);

export const LayerProductionModal: React.FC<LayerProductionModalProps> = ({
  open,
  onClose,
  layerId,
  layerName,
  wells,
  canManage = false,
  onDataUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [historicalProduction, setHistoricalProduction] = useState<HistoricalProductionPoint[]>([]);
  const [defaultProfiles, setDefaultProfiles] = useState<DefaultProfilePoint[]>([]);
  const [profileCoefs, setProfileCoefs] = useState<LayerDefaultWellProfileCoefs | null>(null);
  const [productionImportOpen, setProductionImportOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [layer, coefs] = await Promise.all([
        getLayer(layerId),
        layerDefaultWellProfilesApi.getCoefsByLayer(layerId).catch(() => null),
      ]);

      setHistoricalProduction(
        (layer.production_data ?? []).map((row) => ({
          year: row.year,
          oil: row.oil,
          gas: row.gas,
          liquid: computeLiquid(row.oil, row.water),
          waterCut: computeWaterCut(row.oil, row.water),
        }))
      );

      setProfileCoefs(coefs);
      const profileRows = coefs ? buildDefaultProfilesFromCoefs(coefs) : [];
      setDefaultProfiles(profileRows);
    } catch (err) {
      console.error('Failed to load layer production data:', err);
      setHistoricalProduction([]);
      setDefaultProfiles([]);
      setProfileCoefs(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !layerId) {
      return;
    }
    loadData();
  }, [open, layerId]);

  const handleImported = () => {
    loadData();
    onDataUpdated?.();
  };

  const calendarYears = useMemo(
    () => collectCalendarYears(
      historicalProduction.map((row) => row.year),
      wells,
      layerId,
    ),
    [historicalProduction, wells, layerId],
  );

  const historyChartData = useMemo(
    () => mergeHistoryChartData(
      buildActiveWellsByYear(wells, layerId, calendarYears),
      historicalProduction,
    ),
    [wells, layerId, calendarYears, historicalProduction],
  );

  const hasHistoryChart = historyChartData.some(
    (row) =>
      (row.activeWells != null && row.activeWells > 0) ||
      row.oil != null ||
      row.gas != null ||
      row.liquid != null ||
      row.waterCut != null,
  );
  const hasHistoricalProduction = historicalProduction.length > 0;
  const hasDefaultProfiles = defaultProfiles.some(
    (row) => row.oil != null || row.gas != null || row.liquid != null || row.waterCut != null,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Production — {layerName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">
                  Reservoir history
                </Typography>
                {canManage && (
                  <Button size="small" variant="outlined" onClick={() => setProductionImportOpen(true)}>
                    Import CSV
                  </Button>
                )}
              </Box>

              {hasHistoryChart ? (
                <Box sx={{ height: HISTORY_CHART_HEIGHT, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={historyChartData}
                      margin={{ top: 5, right: 90, left: 55, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="year"
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                      />
                      <YAxis
                        yAxisId="production"
                        label={{ value: PRODUCTION_AXIS_LABEL, angle: -90, position: 'insideLeft' }}
                        stroke={OIL_COLOR}
                      />
                      <YAxis
                        yAxisId="wells"
                        orientation="right"
                        allowDecimals={false}
                        label={{ value: 'Active wells', angle: 90, position: 'insideRight' }}
                        stroke={WELLS_BAR_COLOR}
                        width={45}
                      />
                      <YAxis
                        yAxisId="wc"
                        orientation="right"
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                        label={{ value: 'WC %', angle: 90, position: 'insideRight', offset: -55 }}
                        stroke={WC_COLOR}
                        width={50}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          const numeric = Number(value);
                          if (name === 'Water cut' || name === 'WC %') {
                            return [`${numeric.toFixed(2)}%`, 'Water cut'];
                          }
                          if (name === 'Active wells') {
                            return [numeric.toFixed(0), name];
                          }
                          if (name === 'Oil' || name === 'Gas' || name === 'Liquid') {
                            return [formatThousandVolumeTooltip(numeric, SCENARIO_UNIT_TONS), name];
                          }
                          return [`${numeric.toFixed(2)}`, name];
                        }}
                      />
                      <Legend />
                      <Bar
                        yAxisId="wells"
                        dataKey="activeWells"
                        name="Active wells"
                        fill={WELLS_BAR_COLOR}
                        barSize={18}
                      />
                      <Line
                        yAxisId="production"
                        type="monotone"
                        dataKey="oil"
                        stroke={OIL_COLOR}
                        name="Oil"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                      <Line
                        yAxisId="production"
                        type="monotone"
                        dataKey="gas"
                        stroke={GAS_COLOR}
                        name="Gas"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                      <Line
                        yAxisId="production"
                        type="monotone"
                        dataKey="liquid"
                        stroke={LIQUID_COLOR}
                        name="Liquid"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                      <Line
                        yAxisId="wc"
                        type="monotone"
                        dataKey="waterCut"
                        stroke={WC_COLOR}
                        name="Water cut"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyChartPlaceholder title="Production and active wells" />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">
                  Base well profile
                </Typography>
              </Box>

              {profileCoefs && (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  q₀={profileCoefs.q0?.toFixed(4) ?? '—'}
                  {' · '}
                  a={profileCoefs.a?.toFixed(4) ?? '—'}
                  {' · '}
                  q₀gas={profileCoefs.q0_gas != null ? profileCoefs.q0_gas.toFixed(4) : '—'}
                  {' · '}
                  a_gas={profileCoefs.a_gas != null ? profileCoefs.a_gas.toFixed(4) : '—'}
                  {' · '}
                  wc₀={profileCoefs.wc0 != null ? profileCoefs.wc0.toFixed(4) : '—'}
                  {' · '}
                  k={profileCoefs.k != null ? profileCoefs.k.toFixed(4) : '—'}
                </Typography>
              )}

              {hasDefaultProfiles ? (
                <Box sx={{ height: PROFILE_CHART_HEIGHT, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={defaultProfiles} margin={{ top: 5, right: 50, left: 55, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" label={{ value: 'Year index', position: 'insideBottom', offset: -4 }} />
                      <YAxis
                        yAxisId="rates"
                        label={{ value: PROFILE_RATES_AXIS_LABEL, angle: -90, position: 'insideLeft' }}
                      />
                      <YAxis yAxisId="wc" orientation="right" domain={[0, 100]} />
                      <Tooltip
                        formatter={(value, name) => {
                          const numeric = Number(value);
                          if (name === 'Water cut') {
                            return [`${numeric.toFixed(2)}%`, name];
                          }
                          if (name === 'Oil' || name === 'Gas' || name === 'Liquid') {
                            return [formatThousandVolumeTooltip(numeric, SCENARIO_UNIT_TONS), name];
                          }
                          return [`${numeric.toFixed(2)}`, name];
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="rates"
                        type="monotone"
                        dataKey="oil"
                        stroke={OIL_COLOR}
                        name="Oil"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                      <Line
                        yAxisId="rates"
                        type="monotone"
                        dataKey="gas"
                        stroke={GAS_COLOR}
                        name="Gas"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                      <Line
                        yAxisId="rates"
                        type="monotone"
                        dataKey="liquid"
                        stroke={LIQUID_COLOR}
                        name="Liquid"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                      <Line
                        yAxisId="wc"
                        type="monotone"
                        dataKey="waterCut"
                        stroke={WC_COLOR}
                        name="Water cut"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyChartPlaceholder title="Oil / gas / liquid / water cut" height={PROFILE_CHART_HEIGHT} />
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <LayerProductionImportCsvModal
        open={productionImportOpen}
        onClose={() => setProductionImportOpen(false)}
        layerId={layerId}
        hasExistingData={hasHistoricalProduction}
        onImported={handleImported}
      />
    </Dialog>
  );
};
